import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import CurrencyExchangeIcon from "@mui/icons-material/CurrencyExchange";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import HourglassTopIcon from "@mui/icons-material/HourglassTop";
import ScheduleIcon from "@mui/icons-material/Schedule";
import UndoIcon from "@mui/icons-material/Undo";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { FieldProps, useRecordContext } from "react-admin";
import get from "lodash/get";
import { autovioColors } from "../misc/backofficeTheme";
import type { PaymentStatus } from "../providers/performanceOverviewProvider";
import { Box, Typography } from "@mui/material";

export const PAYMENT_STATUS_LABEL_AND_COLOR: { [status in PaymentStatus]: { label: string; color: string } } = {
  UNPAID: { label: "Unbezahlt", color: autovioColors.yellowUltraLight },
  AWAITING_PAYMENT: { label: "In Arbeit", color: autovioColors.greyUltraLight },
  PAID: { label: "Bezahlt", color: autovioColors.greenLight },
  PAYMENT_REVOKED: { label: "Angefochten", color: autovioColors.orange },
  PARTIALLY_REFUNDED: { label: "Teilweise erstattet", color: autovioColors.yellowUltraLight },
  REFUNDED: { label: "Erstattet", color: autovioColors.greyUltraLight },
  VOIDED: { label: "Storniert", color: autovioColors.greyUltraLight },
  UNKNOWN: { label: "Unbekannt", color: autovioColors.orange },
};

const PAYMENT_STATUS_ICON: { [status in PaymentStatus]: typeof CheckIcon } = {
  UNPAID: ScheduleIcon,
  AWAITING_PAYMENT: HourglassTopIcon,
  PAID: CheckIcon,
  PAYMENT_REVOKED: WarningAmberIcon,
  PARTIALLY_REFUNDED: CurrencyExchangeIcon,
  REFUNDED: UndoIcon,
  VOIDED: CloseIcon,
  UNKNOWN: HelpOutlineIcon,
};

export function PaymentStatusField(props: FieldProps & { source: string; size?: "small" }) {
  const { size } = props;
  const record = useRecordContext(props);
  if (!record) {
    return null;
  }
  const value = get(record, props.source);

  if (!value) {
    return null;
  }

  const status = value as PaymentStatus;
  const { label, color } = PAYMENT_STATUS_LABEL_AND_COLOR[status] ?? {
    label: value,
    color: autovioColors.yellowUltraLight,
  };
  const StatusIcon = PAYMENT_STATUS_ICON[status] ?? WarningAmberIcon;

  return (
    <Box
      style={{
        backgroundColor: color,
        color: "rgba(0, 0, 0, 0.87)",
        padding: size === "small" ? "0 8px" : "0 10px",
        borderRadius: size === "small" ? "999px" : "5px",
        alignItems: "center",
        display: "flex",
        gap: size === "small" ? "4px" : "6px",
        width: "fit-content",
      }}
    >
      <StatusIcon sx={{ flexShrink: 0, fontSize: size === "small" ? "0.875rem" : "1rem" }} />
      <Typography
        variant="overline"
        style={{
          display: size === "small" ? "block" : undefined,
          fontSize: size === "small" ? "0.6875rem" : undefined,
          lineHeight: size === "small" ? "22px" : undefined,
          textTransform: "none",
          whiteSpace: "nowrap",
        }}
      >
        {label}
      </Typography>
    </Box>
  );
}
